<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { RouterView } from 'vue-router'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/userStore'
import { clearStoredSession, ensureAccessToken, SESSION_EXPIRED_EVENT } from '@/utils/authSession'

const router = useRouter()
const userStore = useUserStore()
const activityEvents = [
  'pointermove',
  'pointerdown',
  'keydown',
  'input',
  'change',
  'scroll',
] as const
const ACTIVITY_CHECK_INTERVAL_MS = 60 * 1000
let handlingExpiration = false
let lastActivityCheckAt = 0

const handleExpiredSession = async () => {
  if (handlingExpiration || router.currentRoute.value.name === 'Login') return
  handlingExpiration = true
  clearStoredSession()
  userStore.syncFromStorage()
  alert('您的登入狀態已過期，請重新登入。')
  const redirect = router.currentRoute.value.fullPath
  await router.replace({ name: 'Login', query: { redirect } })
  handlingExpiration = false
}

const refreshWhenReturning = async () => {
  if (!localStorage.getItem('token') || router.currentRoute.value.name === 'Login') return
  const token = await ensureAccessToken()
  if (token) userStore.syncFromStorage()
}

const keepSessionAlive = () => {
  if (
    !localStorage.getItem('token') ||
    router.currentRoute.value.name === 'Login' ||
    document.visibilityState !== 'visible'
  )
    return

  const now = Date.now()
  if (now - lastActivityCheckAt < ACTIVITY_CHECK_INTERVAL_MS) return
  lastActivityCheckAt = now

  // User activity checks locally once a minute; the API is called only near token expiry.
  void ensureAccessToken().then((token) => {
    if (token) userStore.syncFromStorage()
  })
}

const handleStorage = (event: StorageEvent) => {
  if (event.key !== 'token') return
  userStore.syncFromStorage()
  if (!event.newValue) void handleExpiredSession()
}

onMounted(() => {
  window.addEventListener(SESSION_EXPIRED_EVENT, handleExpiredSession)
  window.addEventListener('focus', refreshWhenReturning)
  window.addEventListener('storage', handleStorage)
  activityEvents.forEach((event) =>
    window.addEventListener(event, keepSessionAlive, { capture: true, passive: true }),
  )
})

onUnmounted(() => {
  window.removeEventListener(SESSION_EXPIRED_EVENT, handleExpiredSession)
  window.removeEventListener('focus', refreshWhenReturning)
  window.removeEventListener('storage', handleStorage)
  activityEvents.forEach((event) =>
    window.removeEventListener(event, keepSessionAlive, { capture: true }),
  )
})
</script>

<template>
  <RouterView />
</template>

<style>
/* 這裡可以放全域樣式，先保持空白即可 */
</style>
